import { useEffect, useMemo, useState } from "react"
import { PricingTable } from "components/ui/tables/PricingTable/PricingTable"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { useGetSeries } from "hooks/api/series/series.hook"
import {
	resolveDraftField,
	hasRowAllPrices,
	hasRowAnyPrice,
	type TDraftRow,
} from "components/ui/tables/PricingTable/PricingTable.helper"
import type {
	TModelNumberPricePayloadItem,
	TPriceField,
	TPricingSeries,
	TPricingTableRow,
} from "types/modelNumbers.type"
import style from "./PriceStep.module.scss"

const { priceStep, skeleton } = style

const buildPayload = (rows: TPricingTableRow[]): TModelNumberPricePayloadItem[] => {
	const payload: TModelNumberPricePayloadItem[] = []

	for (const row of rows) {
		if (!hasRowAllPrices(row)) continue

		payload.push({
			series_id: row.series_id,
			min_price: row.min_price,
			avg_price: row.avg_price,
			max_price: row.max_price,
		})
	}

	return payload
}

type Props = {
	onPricesChange?: (prices: TModelNumberPricePayloadItem[]) => void
	onHasIncompletePricesChange?: (hasIncomplete: boolean) => void
	showValidation?: boolean
}

export const PriceStep = ({ onPricesChange, onHasIncompletePricesChange, showValidation }: Props) => {
	const { data: series, isLoading } = useGetSeries()
	const [draftPrices, setDraftPrices] = useState<Record<number, TDraftRow>>({})

	const baseRows = useMemo<TPricingTableRow[]>(() => {
		return (series ?? []).map((seriesItem: TPricingSeries) => ({
			key: seriesItem.id,
			series_id: seriesItem.id,
			series: seriesItem.series_name,
			min_price: undefined,
			avg_price: undefined,
			max_price: undefined,
			hasPrices: false,
		}))
	}, [series])

	const mergedRows = useMemo<TPricingTableRow[]>(() => {
		return baseRows.map((row) => {
			const draftRow = draftPrices[row.series_id]
			if (!draftRow) return row

			const minPrice = resolveDraftField(row.min_price, draftRow, "min_price")
			const avgPrice = resolveDraftField(row.avg_price, draftRow, "avg_price")
			const maxPrice = resolveDraftField(row.max_price, draftRow, "max_price")

			return {
				...row,
				min_price: minPrice,
				avg_price: avgPrice,
				max_price: maxPrice,
				hasPrices: hasRowAnyPrice({ min_price: minPrice, avg_price: avgPrice, max_price: maxPrice }),
			}
		})
	}, [baseRows, draftPrices])

	const invalidSeriesIds = useMemo(() => {
		const ids = new Set<number>()
		for (const row of mergedRows) {
			if (hasRowAnyPrice(row) && !hasRowAllPrices(row)) {
				ids.add(row.series_id)
			}
		}
		return ids
	}, [mergedRows])

	useEffect(() => {
		onPricesChange?.(buildPayload(mergedRows))
		onHasIncompletePricesChange?.(invalidSeriesIds.size > 0)
	}, [mergedRows, onPricesChange, onHasIncompletePricesChange, invalidSeriesIds])

	const onCellValueChangeHandle = (seriesId: number, field: TPriceField, value: number | undefined) => {
		setDraftPrices((prev) => ({
			...prev,
			[seriesId]: { ...(prev[seriesId] ?? {}), [field]: value === undefined ? null : value },
		}))
	}

	if (isLoading) {
		return (
			<div className={skeleton}>
				<Skeleton rows={30} />
			</div>
		)
	}

	return (
		<div className={priceStep}>
			<PricingTable data={mergedRows} onCellValueChange={onCellValueChangeHandle} allRowsEnabled invalidSeriesIds={showValidation ? invalidSeriesIds : undefined} />
		</div>
	)
}
